<div class="w-full rounded-2xl bg-neutral-2/50 border border-neutral-3/70 p-4 sm:p-5 shadow-2xs">
  <div class="grid grid-cols-4 gap-2 sm:gap-4 relative">
    @for (step of steps(); track step.index; let last = $last) {
      <div
        class="group relative flex flex-col items-center text-center"
        [class.cursor-pointer]="step.isClickable"
        (click)="onStepClick(step)"
        (keydown.enter)="onStepClick(step)"
        (keydown.space)="onStepClick(step); $event.preventDefault()"
        [attr.tabindex]="step.isClickable ? 0 : null"
        [attr.role]="step.isClickable ? 'button' : null"
        [matTooltip]="step.clickTooltip || ''"
      >
        <!-- Connecting track to next step (behind node) -->
        @if (!last) {
          <div
            class="absolute top-[18px] sm:top-[22px] left-1/2 w-full h-[3px] -translate-y-1/2 z-0 transition-colors pointer-events-none"
            [class.bg-jade-9]="step.state === 'completed'"
            [class.bg-neutral-3]="step.state !== 'completed'"
          ></div>
        }

        <!-- Step node icon / disc -->
        <div
          class="relative z-10 size-9 sm:size-11 rounded-full flex items-center justify-center transition-all duration-200 select-none"
          [class.bg-jade-9]="step.state === 'completed'"
          [class.shadow-xs]="step.state === 'completed'"
          [class.ring-4]="true"
          [class.ring-jade-3]="step.state === 'completed'"
          [class.bg-indigo-9]="step.state === 'current'"
          [class.shadow-md]="step.state === 'current'"
          [class.ring-indigo-3]="step.state === 'current'"
          [class.bg-neutral-1]="step.state === 'upcoming'"
          [class.border-2]="step.state === 'upcoming'"
          [class.border-neutral-4]="step.state === 'upcoming'"
          [class.ring-neutral-2]="step.state === 'upcoming'"
          [class.bg-red-9]="step.state === 'rejected'"
          [class.ring-red-3]="step.state === 'rejected'"
          [class.bg-neutral-5]="step.state === 'cancelled'"
          [class.ring-neutral-3]="step.state === 'cancelled'"
          [class.hover:scale-110]="step.isClickable"
          [class.active:scale-95]="step.isClickable"
        >
          @switch (step.state) {
            @case ('completed') {
              <mat-icon
                class="!text-white !text-lg sm:!text-xl size-5 sm:size-6 flex items-center justify-center font-bold"
              >
                check
              </mat-icon>
            }
            @case ('current') {
              <mat-icon
                class="!text-white !text-lg sm:!text-xl size-5 sm:size-6 flex items-center justify-center"
              >
                {{ step.icon }}
              </mat-icon>
            }
            @case ('rejected') {
              <mat-icon
                class="!text-white !text-lg sm:!text-xl size-5 sm:size-6 flex items-center justify-center"
              >
                close
              </mat-icon>
            }
            @case ('cancelled') {
              <mat-icon
                class="!text-white !text-lg sm:!text-xl size-5 sm:size-6 flex items-center justify-center"
              >
                block
              </mat-icon>
            }
            @default {
              <span class="text-xs sm:text-sm font-bold text-neutral-11">{{ step.index + 1 }}</span>
            }
          }
        </div>

        <!-- Step typography & labels -->
        <div class="mt-2.5 sm:mt-3 flex flex-col items-center text-center max-w-full px-0.5">
          <div
            class="text-xs sm:text-sm leading-tight truncate max-w-full flex items-center gap-1"
            [class.font-bold]="step.state === 'completed'"
            [class.text-neutral-12]="step.state === 'completed'"
            [class.font-extrabold]="step.state === 'current'"
            [class.text-indigo-12]="step.state === 'current'"
            [class.font-semibold]="step.state === 'upcoming' || step.state === 'cancelled'"
            [class.text-neutral-10]="step.state === 'upcoming' || step.state === 'cancelled'"
            [class.text-red-12]="step.state === 'rejected'"
          >
            <span>{{ step.label }}</span>
            @if (step.isClickable) {
              <mat-icon
                class="!text-xs size-3.5 text-indigo-11 shrink-0 opacity-70 group-hover:opacity-100 transition-opacity"
              >
                open_in_new
              </mat-icon>
            }
          </div>

          <!-- Sublabel / status badge -->
          @if (step.index === 1 && step.isClickable) {
            <!-- Interactive Visit Pill with Date -->
            <div
              class="mt-1 inline-flex items-center gap-1 text-[10px] sm:text-[11px] font-semibold text-indigo-11 bg-indigo-2 hover:bg-indigo-3 active:scale-95 px-1.5 sm:px-2 py-0.5 rounded-md border border-indigo-3/80 shadow-2xs transition-all"
            >
              <mat-icon class="!text-xs size-3 text-indigo-9">calendar_today</mat-icon>
              <span class="truncate max-w-[85px] sm:max-w-none">{{ step.sublabel }}</span>
            </div>
          } @else if (step.state === 'current') {
            <div
              class="mt-1 inline-flex items-center gap-1 text-[10px] sm:text-[11px] font-semibold text-indigo-11 bg-indigo-2 px-1.5 sm:px-2 py-0.5 rounded-md border border-indigo-3/70"
            >
              <span class="size-1.5 rounded-full bg-indigo-9"></span>
              <span class="truncate max-w-[80px] sm:max-w-none">{{ step.sublabel }}</span>
            </div>
          } @else if (step.state === 'completed') {
            <div class="text-[11px] sm:text-xs text-jade-11 font-medium mt-0.5 line-clamp-1 hidden sm:block">
              {{ step.sublabel }}
            </div>
          } @else if (step.state === 'rejected') {
            <div class="text-[11px] sm:text-xs text-red-11 font-medium mt-0.5 hidden sm:block">
              {{ step.sublabel }}
            </div>
          } @else {
            <div class="text-[11px] sm:text-xs text-neutral-9 mt-0.5 line-clamp-1 hidden sm:block">
              {{ step.sublabel }}
            </div>
          }
        </div>
      </div>
    }
  </div>
</div>
